import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { Transaction } from 'src/app/model/transaction';
import { TransactionInterface } from 'src/app/model/transaction-interface';
import { TransactionSum } from 'src/app/model/transactionSum';
import { TransactionSumInterface } from 'src/app/model/transactionSum-interface';
import { environment } from 'src/environments/environment';

/**
 * SupplierService is used to hold/move supplier data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class GreenskyService {
  /** The last-queried list of transactions and transaction sums */
  public lastTransactions: Transaction[] = [];
  public lastTransactionSums: TransactionSum[] = [];

  /**
   * Default constructor for the Greensky service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _toastrService: ToastrService,
  ) {
  }

  /**
   * Get backbook transactions associated with a given card number
   * 
   * @param cardNumber The card number to get transactions for
   * @return A reference to the current suppliers
   */
  public getTransactions(cardNumber: number): Observable<Transaction[]> {
    let subject = new Subject<Transaction[]>();

    // Query the backend for the transactions
    const self = this;
    this._http.get(environment.baseUrl + `/gsky/transactions/${cardNumber}`).subscribe({
      next(data: Object): void {
        // Cast the data into a list of transaction interfaces
        const transactionInterfaces = data as TransactionInterface[];
        if (transactionInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into transactionInterfaces list`);
          self._toastrService.error(`Cannot read transaction data`);
          return;
        }

        // Convert the interfaces to class values
        const transactions: Transaction[] = [];
        for (let intf of transactionInterfaces) {
          transactions.push(new Transaction(intf));
        }

        subject.next(transactions);
        self.lastTransactions = transactions;
        self._toastrService.success(`Successfully queried transactions`);
      },
      error(err: any): void {
        console.error(`[ERR]: failed to query transactions: ${err}`);
        self._toastrService.error(`Failed to query transactions`);
      }
    });

    return subject.asObservable();
  }

  /**
   * Get backbook transaction sum associated with a given card number
   * 
   * @param cardNumber The card number to get transactions sum for
   * @return A reference to the current suppliers
   */
  public getTransactionsSum(cardNumber: number): Observable<TransactionSum[]> {
    let subject = new Subject<TransactionSum[]>();

    // Query the backend for the transactions
    const self = this;
    this._http.get(environment.baseUrl + `/gsky/transactions-sum/${cardNumber}`).subscribe({
      next(data: Object): void {
        // Cast the data into a transaction sum interface
        const transactionSumInterfaces = data as TransactionSumInterface[];
        if (transactionSumInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into list of transactionSumInterface`);
          self._toastrService.error(`Cannot read transaction sum data`);
          return;
        }

        // Convert the interfaces to class values
        const transactionSums: TransactionSum[] = [];
        for (let intf of transactionSumInterfaces) {
          transactionSums.push(new TransactionSum(intf));
        }

        // Add the sums to the subject and return success
        subject.next(transactionSums);
        self.lastTransactionSums = transactionSums;
        self._toastrService.success(`Successfully queried summary`);
      },
      error(err: any): void {
        console.error(`[ERR]: failed to query transactions: ${err}`);
        self._toastrService.error(`Failed to query transactions sum`);
      }
    });

    return subject.asObservable();
  }
}